import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
  BoltIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchCategoriesAsync, 
  selectCategories,
  fetchProductsByFiltersAsync,
  selectAllProducts,
  setSelectedProduct
} from '../features/product/productSlice';
import NavBar from '../features/navbar/Navbar';
import Footer from '../features/common/Footer';
import MainLayout from '../components/MainLayout';

// Category icons map - same as sidebar
const categoryIconMap = {
  'educational platforms': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'educational': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'education': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'learning': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'courses': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'e-learning': { icon: '📚', color: 'bg-blue-500', image: '/learning-management-system.png' },
  'tools': { icon: '⚙️', color: 'bg-gray-600', image: '/settings.png' },
  'utilities': { icon: '⚙️', color: 'bg-gray-600', image: '/settings.png' },
  'software tools': { icon: '⚙️', color: 'bg-gray-600', image: '/settings.png' },
  'music': { icon: '🎵', color: 'bg-green-500', image: '/spotify.png' },
  'spotify': { icon: '🎵', color: 'bg-green-500', image: '/spotify.png' },
  'music tools': { icon: '🎵', color: 'bg-green-500', image: '/spotify.png' },
  'music platforms': { icon: '🎵', color: 'bg-green-500', image: '/spotify.png' },
  'ai tools': { icon: '🤖', color: 'bg-indigo-500', image: '/ai.png' },
  'ai': { icon: '🤖', color: 'bg-indigo-500', image: '/ai.png' },
  'artificial intelligence': { icon: '🤖', color: 'bg-indigo-500', image: '/ai.png' },
  'vpn': { icon: '🌐', color: 'bg-teal-500', image: '/vpn.png' },
  'vpns': { icon: '🌐', color: 'bg-teal-500', image: '/vpn.png' },
  'streaming': { icon: '▶️', color: 'bg-red-500', image: '/stream-video.png' },
  'streaming platforms': { icon: '▶️', color: 'bg-red-500', image: '/stream-video.png' },
  'netflix': { icon: '🎬', color: 'bg-red-600', image: '/stream-video.png' },
  'entertainment': { icon: '🎬', color: 'bg-pink-500', image: '/stream-video.png' },
  'subscriptions': { icon: '▶️', color: 'bg-purple-500', image: '/stream-video.png' },
  'e-commerce': { icon: '🛒', color: 'bg-orange-500', image: '/ecommerce.png' },
  'ecommerce': { icon: '🛒', color: 'bg-orange-500', image: '/ecommerce.png' },
  'microsoft office': { icon: '📄', color: 'bg-red-500', image: null },
  'windows': { icon: '🪟', color: 'bg-blue-500', image: null },
  'antivirus': { icon: '🛡️', color: 'bg-green-500', image: null },
  'adobe': { icon: '🎨', color: 'bg-red-600', image: null },
  'gaming': { icon: '🎮', color: 'bg-orange-500', image: null },
};

const getCategoryData = (label) => {
  const lowerLabel = label?.toLowerCase() || '';
  return categoryIconMap[lowerLabel] || { icon: '📦', color: 'bg-gray-500', image: null };
};

function getMultilingualText(field, language = 'en') {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    const result = field[language] || field['en'] || field['fr'] || Object.values(field)[0] || '';
    return typeof result === 'string' ? result : '';
  }
  return '';
}

// Ad Banner Slides - Add images to Frontend/public/ads/ folder
const adBannersTemplate = [
  {
    id: 1,
    discount: '70% OFF',
    bgColor: 'from-blue-600 to-blue-800',
    image: '/ads/HeroesBanner-Microsoft.png',
    categoryName: 'Tools' // Will match from database
  },
  {
    id: 2,
    discount: '50% OFF',
    bgColor: 'from-sky-500 to-blue-700',
    image: '/ads/HeroeBanner-VPN.png',
    categoryName: 'VPNs' // Will match from database
  },
  {
    id: 3,
    discount: 'Best Prices',
    bgColor: 'from-blue-700 to-blue-900',
    image: '/ads/HeroeBanner-Linkedin.png',
    categoryName: 'Educational-Platforms' // Will match from database
  },
  {
    id: 4,
    discount: 'Special Offers',
    bgColor: 'from-indigo-600 to-purple-700',
    image: '/ads/HeroeBanner-app.png',
    categoryName: 'Streaming Platforms' // Will match from database
  },
];

// Main product categories with large images
const mainCategories = [
  {
    id: 'educational',
    name: 'Educational',
    tagline: 'Learning platforms',
    image: '/Bestselling/Educational.png',
    link: '/products?category=Educational-Platforms',
    bg: 'from-blue-500 to-blue-600'
  },
  {
    id: 'linkedin',
    name: 'LinkedIn Vouchers',
    tagline: 'Boost your career',
    image: '/Bestselling/Linkedlnvouchers_.png',
    link: '/products?category=Tools',
    bg: 'from-blue-700 to-blue-900'
  },
  {
    id: 'vpn',
    name: 'Premium VPN',
    tagline: 'Secure & fast',
    image: '/Bestselling/PremiumVPN_.png',
    link: '/products?category=VPNs',
    bg: 'from-green-500 to-emerald-600'
  },
  {
    id: 'subscriptions',
    name: 'Subscriptions',
    tagline: 'Premium streaming',
    image: '/Bestselling/Subscriptions.png',
    link: '/products?category=Streaming-Platforms',
    bg: 'from-purple-500 to-pink-500'
  },
];

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const categories = useSelector(selectCategories);
  const allProducts = useSelector(selectAllProducts);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [adBanners, setAdBanners] = useState([]);

  useEffect(() => {
    dispatch(fetchCategoriesAsync());
    dispatch(fetchProductsByFiltersAsync({ 
      filter: {}, 
      sort: { _sort: 'discountPercentage', _order: 'desc' },
      pagination: { _page: 1, _limit: 12 }
    }));
  }, [dispatch]);

  // Generate ad banners from database categories
  useEffect(() => {
    if (categories.length > 0) {
      const banners = adBannersTemplate.map(banner => {
        // Find matching category from database
        const matchingCategory = categories.find(
          cat => cat.label?.toLowerCase() === banner.categoryName.toLowerCase() ||
                 cat.value?.toLowerCase() === banner.categoryName.toLowerCase()
        );
        
        return {
          ...banner,
          link: matchingCategory ? `/products?category=${matchingCategory.value}` : '/products'
        };
      });
      setAdBanners(banners);
    }
  }, [categories]);

  // Auto-slide for banner
  useEffect(() => {
    if (adBanners.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % adBanners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [adBanners.length]);

  const handleCategoryClick = (categoryValue) => {
    navigate(`/products?category=${categoryValue}`);
  };

  const nextSlide = () => {
    if (adBanners.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % adBanners.length);
    }
  };
  
  const prevSlide = () => {
    if (adBanners.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + adBanners.length) % adBanners.length);
    }
  };

  return (
    <MainLayout>
      <NavBar>
        <div className="min-h-screen bg-white">
          
          {/* Hero Banner */}
          <section className="bg-white border-b border-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              {/* Ad Banner Slider */}
              {adBanners.length > 0 && (
              <div className="relative">
                <div className="relative h-[280px] sm:h-[320px] lg:h-[360px] rounded-xl overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentSlide}
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                      className={`absolute inset-0 bg-gradient-to-r ${adBanners[currentSlide]?.bgColor} flex items-center`}
                    >
                      {/* Background Image */}
                      {adBanners[currentSlide]?.image && (
                        <img
                          src={adBanners[currentSlide]?.image}
                          alt={adBanners[currentSlide]?.title}
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      {/* Overlay for text readability */}
                      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
                      
                      <div className="relative z-10 px-8 sm:px-12 lg:px-16 text-white max-w-lg">
                        <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-bold mb-4">
                          {adBanners[currentSlide]?.discount}
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
                          {adBanners[currentSlide]?.title}
                        </h2>
                        <p className="text-lg text-white/90 mb-6">
                          {adBanners[currentSlide]?.subtitle}
                        </p>
                        <Link
                          to={adBanners[currentSlide]?.link || '/'}
                          className="inline-flex items-center gap-2 mt-32 px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          Shop Now
                          <ArrowRightIcon className="w-5 h-5" />
                        </Link>
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* Slider Controls */}
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                  >
                    <ArrowLeftIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                  >
                    <ArrowRightIcon className="w-5 h-5" />
                  </button>

                  {/* Slide Indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {adBanners.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          idx === currentSlide ? 'bg-white' : 'bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              )}
            </div>
          </section>

          {/* Trust Bar - Minimal */}
          <section className="bg-gray-50 py-3 border-b border-gray-100">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-center gap-8 text-sm text-gray-600">
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  Genuine keys
                </span>
                <span className="flex items-center gap-1.5">
                  <BoltIcon className="w-4 h-4 text-blue-500" />
                  Instant delivery
                </span>
                <span className="flex items-center gap-1.5">
                <ShieldCheckIcon className="w-4 h-4 text-purple-500" />
                Secure checkout
              </span>
            </div>
          </div>
        </section>

         {/* Browse All Categories */}
        {categories.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">All Categories</h2>
            {/* Horizontal Scrollable on mobile, full width on desktop */}
            <div className="overflow-x-auto pb-4 scrollbar-hide">
              <div className="flex gap-6 lg:gap-8 min-w-max lg:min-w-0 lg:justify-between lg:flex-wrap">

              {categories.map((cat) => {
                const catData = getCategoryData(cat.label);
                // Use productCount from API response, fallback to filtering if not available
                const productCount = cat.productCount !== undefined 
                  ? cat.productCount 
                  : (allProducts?.filter(p => {
                      const pCategory = typeof p.category === 'string' 
                        ? p.category 
                        : (p.category?.en || p.category?.fr || '');
                      return pCategory.toLowerCase() === cat.value?.toLowerCase() ||
                        pCategory.toLowerCase() === cat.label?.toLowerCase();
                    }).length || 0);
                
                return (
                  <button
                    key={cat.value}
                    onClick={() => handleCategoryClick(cat.value)}
                    className="flex flex-col items-center group"
                  >
                    {/* Large Icon Container */}
                    <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mb-3 transition-transform group-hover:scale-110 ${catData.image ? 'bg-gray-100' : catData.color}`}>
                      {catData.image ? (
                        <img 
                          src={catData.image} 
                          alt={cat.label}
                          className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                          onError={(e) => { 
                            e.target.style.display = 'none'; 
                            e.target.parentElement.innerHTML = `<span class="text-4xl">${catData.icon}</span>`;
                          }}
                        />
                      ) : (
                        <span className="text-4xl sm:text-5xl">{catData.icon}</span>
                      )}
                    </div>
                    {/* Category Name */}
                    <span className="text-sm font-medium text-gray-800 text-center group-hover:text-blue-600 transition-colors">
                      {cat.label}
                    </span>
                    {/* Product Count */}
                    <span className="text-xs text-gray-500 mt-1">
                      {productCount} {productCount === 1 ? 'product' : 'products'}
                    </span>
                  </button>
                );
              })}
              </div>
            </div>
          </section>
        )}

        {/* Main Categories - Large Visual Cards */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-12">
                      <h2 className="text-2xl font-bold text-gray-900 mb-8">Best Selling</h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {mainCategories.map((cat) => (
              <Link
                key={cat.id}
                to={cat.link}
                className="group relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="w-full overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              </Link>
            ))}
          </div>
        </section>
 

        {/* Products Grid - Clean Layout */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Popular Products
            </h2>
            <Link 
              to="/" 
              className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 text-sm"
            >
              View all <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>

          {allProducts && allProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {allProducts.slice(0, 8).map((product) => {
                const displayPrice = product.discountPrice || product.price;
                const displayOriginalPrice = product.price;
                const discountPercentage = displayOriginalPrice > 0 
                  ? Math.round(((displayOriginalPrice - displayPrice) / displayOriginalPrice) * 100)
                  : 0;
                
                return (
                  <Link
                    key={product.id}
                    to={`/product-detail/${product.id}`}
                    onClick={() => dispatch(setSelectedProduct(product))}
                    className="group bg-white rounded-xl hover:shadow-lg transition-all border border-gray-100 hover:border-blue-200 overflow-hidden"
                  >
                    {/* Full Size Product Image */}
                    <div className="relative overflow-hidden rounded-t-lg">
                      <img
                        src={product.thumbnail}
                        alt={getMultilingualText(product.title)}
                        className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {discountPercentage > 0 && (
                        <span className="absolute top-0 right-0 bg-red-500 text-white px-2 py-0.5 rounded-bl text-[14px] font-bold shadow-md">
                          -{discountPercentage}%
                        </span>
                      )}
                    </div>
                    
                    {/* Product Info - Short & Light */}
                    <div className="p-4">
                      <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
                        {getMultilingualText(product.title)}
                      </h3>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg font-bold text-blue-600">€{displayPrice}</span>
                        {discountPercentage > 0 && (
                          <span className="text-xs text-gray-400 line-through">€{displayOriginalPrice}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400">Loading products...</p>
            </div>
          )}
        </section>

      
        {/* Simple Value Props */}
        <section className="bg-gray-50 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <img src="/Logos/GENUINE.png" alt="100% Genuine" className="w-full h-full object-contain" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">100% Genuine</h3>
                <p className="text-sm text-gray-500">Official licensed products</p>
              </div>
              <div>
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <img src="/Logos/INSTANTDELIVERY.png" alt="Instant Delivery" className="w-full h-full object-contain" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Instant Delivery</h3>
                <p className="text-sm text-gray-500">Get your key in minutes</p>
              </div>
              <div>
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <img src="/Logos/BESTPRICES.png" alt="Best Prices" className="w-full h-full object-contain" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Best Prices</h3>
                <p className="text-sm text-gray-500">Save up to 70% off retail</p>
              </div>
            </div>
          </div>
        </section>

        </div>
        <Footer />
      </NavBar>
    </MainLayout>
  );
};

export default HomePage;
